import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseService } from '@app/services';
import { DirectivesModule, SharedModule } from '@app/shared';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AccountsService } from '../accounts.service';
import { AuthCodeInputComponent } from '../code-input';
import { formatCountdown, PasswordlessAuthService, startOtpCountdowns } from '../passwordless';
import { AuthShellComponent } from '../shell';
import { OtpFormInterface, OtpFormValuesInterface } from './otp.interface';

/**
 * Login with a 6-digit e-mail OTP after the address has been confirmed:
 * step 1 asks for the e-mail, step 2 is the two-factor style code entry.
 */
@Component({
  imports: [SharedModule, DirectivesModule, AuthShellComponent, AuthCodeInputComponent],
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpComponent extends BaseService implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly accountsService = inject(AccountsService);
  private readonly passwordlessAuthService = inject(PasswordlessAuthService);

  formOtp: FormGroup;
  protected readonly remainingSeconds = signal<number>(0);
  protected readonly validitySeconds = signal<number>(0);
  protected readonly hasRequestedCode = signal<boolean>(false);
  protected readonly formatCountdown = formatCountdown;
  private stopCooldown: (() => void) | null = null;

  ngOnInit(): void {
    this.createForm();
  }

  ngOnDestroy(): void {
    this.stopCooldown?.();
  }

  createForm(): void {
    this.formOtp = new FormGroup<OtpFormInterface>({
      email: new FormControl('', [RxwebValidators.required(), RxwebValidators.email()]),
      code: new FormControl('', [RxwebValidators.required(), RxwebValidators.minLength({ value: 6 }), RxwebValidators.maxLength({ value: 6 })]),
    });
  }

  async requestCode(): Promise<void> {
    if (this.remainingSeconds() > 0) {
      return;
    }
    const emailControl = this.formOtp.get('email');
    if (emailControl?.invalid) {
      return;
    }
    const formValues: OtpFormValuesInterface = this.formOtp.getRawValue();
    this._spin.start();
    await this.passwordlessAuthService
      .requestEmailOtp(formValues.email)
      .then(() => {
        this.formOtp.get('code')?.reset('');
        this.hasRequestedCode.set(true);
        this.beginCooldown();
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  async verifyCode(): Promise<void> {
    const formValues: OtpFormValuesInterface = this.formOtp.getRawValue();
    this._spin.start();
    await this.passwordlessAuthService
      .verifyEmailOtp(formValues.email, formValues.code)
      .then(async (): Promise<void> => {
        await this.accountsService.loadAccountIds();
        this._toast.success({ message: 'Login realizado com sucesso!' });
        void this.router.navigateByUrl('/dashboard');
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  protected changeEmail(): void {
    this.formOtp.get('code')?.reset('');
    this.hasRequestedCode.set(false);
  }

  private beginCooldown(): void {
    this.stopCooldown?.();
    this.stopCooldown = startOtpCountdowns((state): void => {
      this.remainingSeconds.set(state.resendSeconds);
      this.validitySeconds.set(state.validitySeconds);
    });
  }
}
